package com.dotdrives.web.pages

import com.dotdrives.controller.AuthController
import com.dotdrives.controller.AuthUiState
import com.dotdrives.model.AuthAudience
import com.dotdrives.model.AuthFormState
import com.dotdrives.model.AuthProvider
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import com.dotdrives.web.ui.DotTextField
import kotlinx.browser.window
import react.ChildrenBuilder
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.br
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.strong
import react.useState
import web.cssom.ClassName
import web.dom.ElementId
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.email
import web.html.password
import web.html.text
import web.html.submit

external interface SignInPageProps : Props {
    var controller: AuthController
    var state: AuthUiState
}

val SignInPage = FC<SignInPageProps> { props ->
    val (email, setEmail) = useState("")
    val (password, setPassword) = useState("")
    val (passwordVisible, setPasswordVisible) = useState(false)
    val (localError, setLocalError) = useState<String?>(null)
    val state = props.state
    val baseUrl = "${window.location.origin}${window.location.pathname}"
    val orgCallback = com.dotdrives.web.organizationCallbackUrl(baseUrl, window.location.hash, window.location.search)
    val inviteCallback = com.dotdrives.web.inviteCallbackUrl(baseUrl, com.dotdrives.web.inviteIdFromLocation(window.location.hash, window.location.search))

    fun changeEmail(value: String) {
        setEmail(value)
        setLocalError(null)
        props.controller.clearError()
    }

    fun changePassword(value: String) {
        setPassword(value)
        setLocalError(null)
        props.controller.clearError()
    }

    fun submitCredentials() {
        val validationError = validateCredentials(email, password)
        setLocalError(validationError)
        if (validationError == null) {
            if (state.formState == AuthFormState.SignUp) {
                props.controller.signUp(email.trim(), password, inviteCallback ?: orgCallback)
            } else {
                props.controller.signIn(email.trim(), password)
            }
        }
    }

    fun sendMagicLink(giverContext: Boolean) {
        val address = email.trim()
        val validationError = validateEmail(address)
        setLocalError(validationError)
        if (validationError == null) {
            props.controller.sendMagicLink(
                email = address,
                redirectUrl = inviteCallback ?: orgCallback ?: magicLinkCallbackUrl(baseUrl, giverContext),
                giverContext = giverContext,
            )
        }
    }

    fun resendMagicLink() {
        if (!state.emailCooldownActive) {
            props.controller.sendMagicLink(
                email = state.pendingEmail,
                redirectUrl = inviteCallback ?: orgCallback ?: magicLinkCallbackUrl(baseUrl, state.giverContext),
                giverContext = state.giverContext,
            )
        }
    }

    fun sendPasswordReset() {
        val address = email.trim()
        val validationError = validateEmail(address)
        setLocalError(validationError)
        if (validationError == null) {
            props.controller.sendPasswordReset(
                email = address,
                redirectUrl = passwordResetCallbackUrl(baseUrl),
            )
        }
    }

    fun resendPasswordReset() {
        if (!state.emailCooldownActive) {
            props.controller.sendPasswordReset(
                email = state.pendingEmail,
                redirectUrl = passwordResetCallbackUrl(baseUrl),
            )
        }
    }

    fun signInWithProvider(provider: AuthProvider, giverContext: Boolean) {
        setLocalError(null)
        props.controller.clearError()
        props.controller.signInWithProvider(
            provider = provider,
            redirectUrl = inviteCallback ?: orgCallback ?: magicLinkCallbackUrl(baseUrl, giverContext),
            giverContext = giverContext,
        )
    }

    div {
        className = ClassName("signin-page")
        StoryPanel()
        div {
            className = ClassName("signin-stage")
            mobileBrand()
            div {
                key = "${state.audience}-${state.formState}-${state.credentialError}-${state.error != null}".unsafeCast<Key>()
                className = ClassName("signin-card")
                when {
                    state.formState == AuthFormState.MagicLinkSent ->
                        sentView(
                            giverContext = state.giverContext,
                            email = state.pendingEmail,
                            secondsRemaining = state.resendSecondsRemaining,
                            loading = state.loading,
                            onResend = ::resendMagicLink,
                            onBack = {
                                if (state.giverContext) {
                                    props.controller.selectAudience(AuthAudience.Giver)
                                } else {
                                    props.controller.showSignIn()
                                }
                            },
                        )

                    state.formState == AuthFormState.PasswordResetSent ->
                        resetSentView(
                            email = state.pendingEmail,
                            secondsRemaining = state.resendSecondsRemaining,
                            loading = state.loading,
                            onResend = ::resendPasswordReset,
                            onBack = props.controller::showSignIn,
                        )

                    state.formState == AuthFormState.ConfirmationSent ->
                        confirmationSentView(
                            email = state.pendingEmail,
                            onBack = props.controller::showSignIn,
                        )

                    state.formState == AuthFormState.ForgotPassword ->
                        forgotPasswordForm(
                            email = email,
                            loading = state.loading,
                            blocked = state.emailCooldownActive,
                            error = localError ?: state.error,
                            onEmailChanged = ::changeEmail,
                            onSend = ::sendPasswordReset,
                            onBack = props.controller::showSignIn,
                        )

                    state.audience == AuthAudience.Giver -> {
                        audienceToggle(state.audience, props.controller::selectAudience)
                        giverForm(
                            email = email,
                            loading = state.loading,
                            blocked = state.emailCooldownActive,
                            error = localError ?: state.error,
                            onEmailChanged = ::changeEmail,
                            onSend = { sendMagicLink(giverContext = true) },
                            onGoogle = { signInWithProvider(AuthProvider.Google, giverContext = true) },
                        )
                    }

                    else -> {
                        if (state.formState == AuthFormState.SignIn) {
                            audienceToggle(state.audience, props.controller::selectAudience)
                        }
                        credentialForm(
                            invitation = inviteCallback != null,
                            formState = state.formState,
                            email = email,
                            password = password,
                            passwordVisible = passwordVisible,
                            loading = state.loading,
                            blocked = state.emailCooldownActive,
                            error = localError ?: state.error,
                            credentialError = state.credentialError,
                            onEmailChanged = ::changeEmail,
                            onPasswordChanged = ::changePassword,
                            onTogglePassword = { setPasswordVisible(!passwordVisible) },
                            onSubmit = ::submitCredentials,
                            onToggleMode = {
                                if (state.formState == AuthFormState.SignUp) {
                                    props.controller.showSignIn()
                                } else {
                                    props.controller.showSignUp()
                                }
                            },
                            onForgot = props.controller::showForgotPassword,
                            onProvider = { signInWithProvider(it, giverContext = false) },
                            onMagicLink = { sendMagicLink(giverContext = false) },
                        )
                    }
                }
            }
        }
    }
}

private val StoryPanel = FC<Props> {
    div {
        className = ClassName("signin-story")
        div { className = ClassName("signin-story-dot signin-story-dot-orange") }
        div { className = ClassName("signin-story-dot signin-story-dot-blue") }
        div { className = ClassName("signin-story-dot signin-story-dot-green") }
        a {
            href = "#/"
            ariaLabel = "Dot Drives home"
            className = ClassName("signin-story-brand dot-home-link")
            +"dot drives"
            span {
                className = ClassName("signin-story-brand-period")
                +"."
            }
            span {
                className = ClassName("signin-story-brand-dot")
                ariaHidden = true
            }
        }
        div {
            className = ClassName("signin-story-content")
            h1 {
                className = ClassName("signin-story-title dot-heading")
                +"Fundraising"
                br {}
                +"that moves."
            }
            p {
                className = ClassName("signin-story-copy")
                +"Every donor has a next step. Sign in and see yours."
            }
            driveTeaser()
        }
        div {
            className = ClassName("signin-story-copyright")
            +"© 2026 Dot Drives"
        }
    }
}

private fun ChildrenBuilder.mobileBrand() {
    div {
        className = ClassName("signin-mobile-brand")
        a {
            href = "#/"
            ariaLabel = "Dot Drives home"
            className = ClassName("signin-mobile-logo dot-home-link")
            +"dot drives"
            span { +"." }
        }
        h1 {
            className = ClassName("signin-mobile-title dot-heading")
            +"Fundraising that moves."
        }
    }
}

private fun ChildrenBuilder.driveTeaser() {
    div {
        className = ClassName("signin-drive-card")
        div {
            className = ClassName("signin-drive-title")
            +"The Miller Family · Clean Water Fund"
        }
        div {
            className = ClassName("signin-drive-meta")
            +"DRIVE #142 · \$25,000 PROPOSED"
        }
        div {
            className = ClassName("signin-drive-track")
            div { className = ClassName("signin-drive-current") }
        }
        div {
            className = ClassName("signin-drive-phases")
            listOf("Identify", "Connect", "Ask", "Close", "Steward").forEach { phase ->
                span { +phase }
            }
        }
    }
}

private fun ChildrenBuilder.audienceToggle(
    audience: AuthAudience,
    onChange: (AuthAudience) -> Unit,
) {
    div {
        className = ClassName("signin-audience")
        button {
            type = ButtonType.buttonType
            className = ClassName(
                "signin-audience-option" +
                    if (audience == AuthAudience.Team) " signin-audience-option-active" else "",
            )
            onClick = { onChange(AuthAudience.Team) }
            +"🧭 I work here"
        }
        button {
            type = ButtonType.buttonType
            className = ClassName(
                "signin-audience-option" +
                    if (audience == AuthAudience.Giver) " signin-audience-option-active signin-audience-giver-active" else "",
            )
            onClick = { onChange(AuthAudience.Giver) }
            +"💛 I'm a giver"
        }
    }
}

private fun ChildrenBuilder.credentialForm(
    invitation: Boolean,
    formState: AuthFormState,
    email: String,
    password: String,
    passwordVisible: Boolean,
    loading: Boolean,
    blocked: Boolean,
    error: String?,
    credentialError: Boolean,
    onEmailChanged: (String) -> Unit,
    onPasswordChanged: (String) -> Unit,
    onTogglePassword: () -> Unit,
    onSubmit: () -> Unit,
    onToggleMode: () -> Unit,
    onForgot: () -> Unit,
    onProvider: (AuthProvider) -> Unit,
    onMagicLink: () -> Unit,
) {
    val signUp = formState == AuthFormState.SignUp
    h2 {
        className = ClassName("signin-title dot-heading")
        +if (invitation) { if (signUp) "Create an account to accept your invitation" else "Sign in to accept your invitation" } else if (signUp) "Start your free trial" else "Welcome back"
    }
    div {
        className = ClassName("signin-new-copy")
        +if (signUp) {
            if (invitation) "Join your team with your invited email. Already have an account? " else "14 days, every feature, no card. Already have an account? "
        } else {
            "New to Dot Drives? "
        }
        button {
            type = ButtonType.buttonType
            className = ClassName("signin-inline-link")
            onClick = { onToggleMode() }
            +if (signUp) "Sign in" else if (invitation) "Create an account" else "Start your free trial"
        }
    }
    error?.let {
        DotBanner {
            message = it
            className = "signin-banner"
        }
    }
    providerButton(
        provider = AuthProvider.Google,
        text = if (signUp) "Sign up with Google" else "Continue with Google",
        loading = loading,
        onProvider = onProvider,
    )
    providerButton(
        provider = AuthProvider.Microsoft,
        text = if (signUp) "Sign up with Microsoft" else "Continue with Microsoft",
        loading = loading,
        onProvider = onProvider,
    )
    divider()
    form {
        className = ClassName("signin-form")
        this.onSubmit = {
            it.preventDefault()
            if (!loading) onSubmit()
        }
        DotTextField {
            value = email
            onValueChange = onEmailChanged
            label = "Work email"
            placeholder = "sarah@hopecollective.org"
            type = InputType.email
            className = if (credentialError) "signin-credential-error" else null
        }
        div {
            className = ClassName(
                "signin-password-group" +
                    if (credentialError) " signin-credential-error" else "",
            )
            div {
                className = ClassName("signin-password-label-row")
                label {
                    className = ClassName("dot-field-label dot-label")
                    htmlFor = ElementId("signin-password")
                    +if (signUp) "Create password" else "Password"
                }
                if (!signUp) {
                    button {
                        type = ButtonType.buttonType
                        className = ClassName("signin-forgot")
                        onClick = { onForgot() }
                        +"Forgot?"
                    }
                }
            }
            div {
                className = ClassName("signin-password-field")
                input {
                    id = ElementId("signin-password")
                    className = ClassName("dot-field")
                    value = password
                    placeholder = if (signUp) "12+ characters with a number" else "Your password"
                    type = if (passwordVisible) InputType.text else InputType.password
                    onChange = { onPasswordChanged(it.currentTarget.value) }
                }
                button {
                    type = ButtonType.buttonType
                    className = ClassName("signin-password-toggle")
                    ariaLabel = if (passwordVisible) "Hide password" else "Show password"
                    onClick = { onTogglePassword() }
                    +if (passwordVisible) "◉" else "◌"
                }
            }
            if (signUp && password.isNotEmpty()) {
                passwordStrength(password)
            }
        }
        DotButton {
            text = if (signUp) "Create account →" else "Sign in"
            type = ButtonType.submit
            this.loading = loading
            className = "dot-full-width signin-primary"
        }
    }
    if (signUp) {
        div {
            className = ClassName("signin-terms")
            +"By continuing you agree to the "
            deadLink("Terms")
            +" and "
            deadLink("Privacy Policy")
            +"."
        }
    } else {
        button {
            type = ButtonType.buttonType
            className = ClassName("signin-magic-button")
            disabled = loading || blocked
            onClick = { onMagicLink() }
            +"✨ Email me a magic link instead"
        }
        authFooter()
    }
}

private fun ChildrenBuilder.giverForm(
    email: String,
    loading: Boolean,
    blocked: Boolean,
    error: String?,
    onEmailChanged: (String) -> Unit,
    onSend: () -> Unit,
    onGoogle: () -> Unit,
) {
    h2 {
        className = ClassName("signin-title dot-heading")
        +"See your giving"
    }
    p {
        className = ClassName("signin-giver-copy")
        +"Gift history, tax receipts, and your monthly giving — no password needed."
    }
    error?.let {
        DotBanner {
            message = it
            className = "signin-banner"
        }
    }
    form {
        className = ClassName("signin-form signin-giver-form")
        this.onSubmit = {
            it.preventDefault()
            onSend()
        }
        DotTextField {
            value = email
            onValueChange = onEmailChanged
            label = "Email you've given with"
            placeholder = "you@email.com"
            type = InputType.email
        }
        DotButton {
            text = "✨ Email me a secure link"
            onClick = onSend
            enabled = !blocked
            this.loading = loading
            className = "dot-full-width"
        }
    }
    divider()
    button {
        type = ButtonType.buttonType
        className = ClassName("signin-provider-button signin-giver-google")
        disabled = loading
        onClick = { onGoogle() }
        googleLogo()
        +"Continue with Google"
    }
    div {
        className = ClassName("signin-info-note signin-giver-note")
        +"💡 Works with any email you've used to give at a Dot Drives organization — we'll show all your giving in one place."
    }
}

private fun ChildrenBuilder.forgotPasswordForm(
    email: String,
    loading: Boolean,
    blocked: Boolean,
    error: String?,
    onEmailChanged: (String) -> Unit,
    onSend: () -> Unit,
    onBack: () -> Unit,
) {
    h2 {
        className = ClassName("signin-title signin-aux-title dot-heading")
        +"Reset your password"
    }
    p {
        className = ClassName("signin-help-copy")
        +"Enter your email and we'll send a reset link."
    }
    error?.let {
        DotBanner {
            message = it
            className = "signin-banner"
        }
    }
    form {
        className = ClassName("signin-form")
        this.onSubmit = {
            it.preventDefault()
            onSend()
        }
        DotTextField {
            value = email
            onValueChange = onEmailChanged
            label = "Work email"
            placeholder = "sarah@hopecollective.org"
            type = InputType.email
        }
        DotButton {
            text = "Send reset link"
            onClick = onSend
            enabled = !blocked
            this.loading = loading
            className = "dot-full-width"
        }
    }
    button {
        type = ButtonType.buttonType
        className = ClassName("signin-back-link")
        onClick = { onBack() }
        +"← Back to sign in"
    }
    div {
        className = ClassName("signin-info-note")
        +"💡 Signed up with Google or Microsoft? Just use that button — no password exists for your account."
    }
}

private fun ChildrenBuilder.sentView(
    giverContext: Boolean,
    email: String,
    secondsRemaining: Int,
    loading: Boolean,
    onResend: () -> Unit,
    onBack: () -> Unit,
) {
    div {
        className = ClassName("signin-sent")
        div {
            className = ClassName("signin-sent-icon")
            +if (giverContext) "💌" else "📬"
        }
        h2 {
            className = ClassName("signin-title signin-aux-title dot-heading")
            +if (giverContext) "Your link is on its way" else "Check your inbox"
        }
        p {
            className = ClassName("signin-sent-copy")
            +if (giverContext) {
                "We sent a secure sign-in link to"
            } else {
                "We sent a sign-in link to"
            }
            br {}
            strong { +email }
            if (!giverContext) {
                br {}
                +"It works for 10 minutes."
            }
        }
        if (giverContext) {
            div {
                className = ClassName("signin-info-note signin-giver-sent-note")
                +"Giving with "
                strong { +"Hope Collective" }
                +" and "
                strong { +"Riverbend Missions" }
                +"? One link opens both — pick after you're in."
            }
        }
        resendMessage(secondsRemaining, loading, onResend)
        button {
            type = ButtonType.buttonType
            className = ClassName("signin-back-link")
            onClick = { onBack() }
            +if (giverContext) "← Try another email" else "← Use a password instead"
        }
    }
}

private fun ChildrenBuilder.resetSentView(
    email: String,
    secondsRemaining: Int,
    loading: Boolean,
    onResend: () -> Unit,
    onBack: () -> Unit,
) {
    div {
        className = ClassName("signin-sent")
        div {
            className = ClassName("signin-sent-icon")
            +"📬"
        }
        h2 {
            className = ClassName("signin-title signin-aux-title dot-heading")
            +"Check your inbox"
        }
        p {
            className = ClassName("signin-sent-copy")
            +"We sent a password reset link to"
            br {}
            strong { +email }
            br {}
            +"It works for 10 minutes."
        }
        resendMessage(secondsRemaining, loading, onResend)
        button {
            type = ButtonType.buttonType
            className = ClassName("signin-back-link")
            onClick = { onBack() }
            +"← Back to sign in"
        }
    }
}

private fun ChildrenBuilder.confirmationSentView(email: String, onBack: () -> Unit) {
    div {
        className = ClassName("signin-sent")
        div {
            className = ClassName("signin-sent-icon")
            +"📬"
        }
        h2 {
            className = ClassName("signin-title signin-aux-title dot-heading")
            +"Check your inbox"
        }
        p {
            className = ClassName("signin-sent-copy")
            +"We sent a confirmation link to"
            br {}
            strong { +email }
            br {}
            +"Open it to finish creating your account."
        }
        button {
            type = ButtonType.buttonType
            className = ClassName("signin-back-link")
            onClick = { onBack() }
            +"← Back to sign in"
        }
    }
}

private fun ChildrenBuilder.resendMessage(
    secondsRemaining: Int,
    loading: Boolean,
    onResend: () -> Unit,
) {
    div {
        className = ClassName("signin-resend")
        +"Didn't get it? Check spam, or "
        button {
            type = ButtonType.buttonType
            className = ClassName("signin-inline-link signin-resend-link")
            disabled = loading || secondsRemaining > 0
            onClick = { onResend() }
            +if (secondsRemaining > 0) {
                "resend in ${formatCountdown(secondsRemaining)}"
            } else {
                "resend now"
            }
        }
    }
}

private fun ChildrenBuilder.providerButton(
    provider: AuthProvider,
    text: String,
    loading: Boolean,
    onProvider: (AuthProvider) -> Unit,
) {
    button {
        type = ButtonType.buttonType
        className = ClassName("signin-provider-button")
        disabled = loading
        onClick = { onProvider(provider) }
        if (provider == AuthProvider.Google) googleLogo() else microsoftLogo()
        +text
    }
}

private fun ChildrenBuilder.divider() {
    div {
        className = ClassName("signin-divider")
        span { +"OR" }
    }
}

private fun ChildrenBuilder.passwordStrength(password: String) {
    val strength = when {
        password.length >= 12 && password.any(Char::isDigit) -> 3
        password.length >= 8 -> 2
        else -> 1
    }
    div {
        className = ClassName("signin-strength")
        div {
            className = ClassName("signin-strength-bars")
            repeat(4) { index ->
                span {
                    className = ClassName(
                        if (index < strength) "signin-strength-active" else "",
                    )
                }
            }
        }
        div {
            className = ClassName("signin-strength-copy")
            +if (strength >= 3) {
                "Strong — 12+ characters with a number ✓"
            } else {
                "Use 12+ characters with a number"
            }
        }
    }
}

private fun ChildrenBuilder.authFooter() {
    div {
        className = ClassName("signin-footer")
        +"Protected by Supabase Auth · "
        deadLink("Privacy")
        +" · "
        deadLink("Terms")
    }
}

private fun ChildrenBuilder.deadLink(text: String) {
    a {
        href = "#"
        onClick = { it.preventDefault() }
        +text
    }
}

private fun ChildrenBuilder.googleLogo() {
    span {
        className = ClassName("signin-google-logo")
        ariaHidden = true
        +"G"
    }
}

private fun ChildrenBuilder.microsoftLogo() {
    span {
        className = ClassName("signin-ms-logo")
        ariaHidden = true
        repeat(4) { span {} }
    }
}

internal fun formatCountdown(seconds: Int): String =
    "${seconds / 60}:${(seconds % 60).toString().padStart(2, '0')}"

internal fun magicLinkCallbackUrl(baseUrl: String, giverContext: Boolean): String =
    if (giverContext) "$baseUrl?giver=1" else "$baseUrl?signin=1"

internal fun passwordResetCallbackUrl(baseUrl: String): String =
    "$baseUrl?recovery=1"

internal fun validateCredentials(email: String, password: String): String? = when {
    email.isBlank() -> "Enter your email address."
    '@' !in email -> "Enter a valid email address."
    password.isBlank() -> "Enter your password."
    else -> null
}

private fun validateEmail(email: String): String? = when {
    email.isBlank() -> "Enter your email address."
    '@' !in email -> "Enter a valid email address."
    else -> null
}
